import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, List, Activity, Mail, UserCheck, ClipboardList, Sprout } from 'lucide-react';

const AdminHub = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const adminSections = [
    {
      title: 'Staff Management',
      description: 'Manage warehouse staff, PINs, and work schedules for check-in/out',
      icon: UserCheck,
      path: '/staff',
      color: '#0ea5e9'
    },
    {
      title: 'Attendance',
      description: 'View daily attendance, check-in/out photos, and date range reports',
      icon: ClipboardList,
      path: '/attendance',
      color: '#f97316'
    },
    {
      title: 'User Management',
      description: 'Create, edit, and manage user accounts and permissions',
      icon: Users,
      path: '/users',
      color: '#3b82f6'
    },
    {
      title: 'Growers',
      description: 'Manage grower codes, names, email and phone — used across pallet entry and reports',
      icon: Sprout,
      path: '/growers',
      color: '#059669'
    },
    {
      title: 'Dropdown Lists',
      description: 'Manage produce types, classes, sizes, and colors',
      icon: List,
      path: '/dropdowns',
      color: '#10b981'
    },
    {
      title: 'Email Recipients',
      description: 'Manage email recipients for pallet sheet distribution',
      icon: Mail,
      path: '/email-recipients',
      color: '#f59e0b'
    },
    {
      title: 'Audit Trail',
      description: 'View system activity logs and track changes',
      icon: Activity,
      path: '/audit',
      color: '#8b5cf6'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/pallets')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Pallet Sheets
              </button>
              <button
                onClick={() => navigate('/reports')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Reports
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#10b981',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Admin
                </button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ marginBottom: '8px', fontSize: '28px', color: '#111827' }}>Admin Center</h2>
          <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
            Manage system settings, users, and view audit trails
          </p>
        </div>

        {/* Admin Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px'
        }}>
          {adminSections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.path}
                onClick={() => navigate(section.path)}
                style={{
                  backgroundColor: 'white',
                  padding: '32px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '2px solid transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = section.color;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: `${section.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <Icon size={24} color={section.color} strokeWidth={2} />
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  {section.title}
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.5'
                }}>
                  {section.description}
                </p>
              </button>
            );
          })}
        </div>

        {/* Info Banner */}
        <div style={{
          marginTop: '40px',
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          border: '2px solid #fbbf24',
          backgroundColor: '#fef3c7'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#fbbf24',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
            </div>
            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                Admin Access Only
              </h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#78350f', lineHeight: '1.5' }}>
                These features are restricted to administrators only. All actions performed here are logged in the audit trail for security and compliance purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHub;
